import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Mail, Phone, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setLanguage } from "@/lib/i18n";
import logo from "@/assets/logo.webp";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").startsWith("it") ? "it" : "en";
  const nav = [
    { to: "/", label: t("header.nav.home") },
    { to: "/strategic-consulting", label: t("header.nav.strategic") },
    { to: "/logistics-transport", label: t("header.nav.logistics") },
    { to: "/services", label: t("header.nav.services") },
    { to: "/about", label: t("header.nav.about") },
    { to: "/news", label: t("header.nav.news") },
    { to: "/contact", label: t("header.nav.contact") },
  ] as const;

  const LangSwitch = (
    <span className="hidden sm:inline-flex items-center gap-2 tracking-widest">
      <button
        type="button"
        onClick={() => setLanguage("en")}
        className={`transition-colors hover:text-primary ${lang === "en" ? "text-primary" : ""}`}
        aria-label="Switch to English"
      >
        EN
      </button>
      <span aria-hidden="true">·</span>
      <button
        type="button"
        onClick={() => setLanguage("it")}
        className={`transition-colors hover:text-primary ${lang === "it" ? "text-primary" : ""}`}
        aria-label="Passa all'italiano"
      >
        IT
      </button>
    </span>
  );

  return (
    <header className="absolute top-0 left-0 right-0 z-50">
      <div className="border-b border-border/30">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-6">
            <Link to="/contact" className="flex items-center gap-2 hover:text-primary transition-colors">
              <Mail className="h-3.5 w-3.5" /><span>sales@comm42.eu</span>
            </Link>
            <a href="tel:+41918311215" className="hidden sm:flex items-center gap-2 hover:text-primary transition-colors">
              <Phone className="h-3.5 w-3.5" /><span>+41 0918311215</span>
            </a>
          </div>
          {LangSwitch}
        </div>
      </div>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-3" aria-label="Comm42 home">
          <img src={logo} alt="Comm42" width={48} height={48} className="h-10 w-10 object-contain" />
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {nav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="text-[11px] tracking-[0.2em] text-foreground/80 hover:text-primary transition-colors"
              activeProps={{ className: "text-[11px] tracking-[0.2em] text-primary" }}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <button
          className="lg:hidden text-foreground"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-border/30 bg-background/95 backdrop-blur">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4">
            {nav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="py-2 text-sm tracking-[0.2em] text-foreground/80 hover:text-primary"
              >
                {item.label}
              </Link>
            ))}
            <div className="mt-3 flex items-center gap-3 border-t border-border/30 pt-3 text-xs tracking-widest text-muted-foreground">
              <button
                type="button"
                onClick={() => { setLanguage("en"); setOpen(false); }}
                className={lang === "en" ? "text-primary" : "hover:text-primary"}
              >EN</button>
              <span aria-hidden="true">·</span>
              <button
                type="button"
                onClick={() => { setLanguage("it"); setOpen(false); }}
                className={lang === "it" ? "text-primary" : "hover:text-primary"}
              >IT</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}